import { useQuery } from '@tanstack/react-query';

import { companyApi } from '@/api';
import { Badge, Skeleton } from '@/components/ui';
import { RECOMMENDATION_LABELS, RECOMMENDATION_TONES } from '@/features/interviews/status';
import { formatDate } from '@/utils/format';

/**
 * What the panel wrote about one round, under the round in the application's
 * rail. List rows carry only the count and average, so the notes are read from
 * the round's own detail -- the same query the feedback dialog uses -- and only
 * for a round that has any. Internal: this never reaches the candidate.
 */
export function RoundNotes({ interview }) {
  const hasFeedback = interview.feedback_count > 0;
  const { data, isPending, isError } = useQuery({
    queryKey: ['company-interview', interview.id],
    queryFn: () => companyApi.getInterview(interview.id),
    enabled: hasFeedback,
  });

  if (interview.status === 'CANCELLED' && interview.cancellation_reason) {
    return (
      <p className="text-ink-muted bg-surface-muted mt-2 rounded-lg px-2.5 py-2 text-xs break-words whitespace-pre-line">
        <span className="text-ink font-medium">Cancelled: </span>
        {interview.cancellation_reason}
      </p>
    );
  }
  if (!hasFeedback) return null;
  if (isPending) return <Skeleton className="mt-2 h-12 w-full" />;
  if (isError) {
    return <p className="text-ink-muted mt-2 text-xs">The panel's notes could not be loaded.</p>;
  }

  return (
    <ul className="mt-2 space-y-2" aria-label={`Feedback on round ${interview.round_number}`}>
      {data.feedback.map((entry) => (
        <li key={entry.id} className="bg-surface-muted rounded-lg px-2.5 py-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-ink text-xs font-semibold tabular-nums">{entry.rating}/5</span>
            <Badge tone={RECOMMENDATION_TONES[entry.recommendation] ?? 'neutral'}>
              {RECOMMENDATION_LABELS[entry.recommendation] ?? entry.recommendation}
            </Badge>
            <span className="text-ink-muted min-w-0 text-xs break-words">
              {entry.author_name ?? 'A former teammate'} · {formatDate(entry.created_at)}
            </span>
          </div>
          {entry.comment ? (
            <p className="text-ink/85 mt-1.5 text-xs leading-relaxed break-words whitespace-pre-line">
              {entry.comment}
            </p>
          ) : (
            <p className="text-ink-muted mt-1 text-xs italic">No notes written.</p>
          )}
        </li>
      ))}
    </ul>
  );
}
